"use strict";

const { countFittingSwatches } = require("../helpers/colourSwatchFit");

const SWATCH_SELECTOR = ".tile-colour-swatch";
const SWATCHES_SELECTOR = ".tile-colour-swatches";
// The containers PLP and Page Designer product lists swap tiles within; only those tiles have swatches
const RESULTS_SELECTOR = ".search-results";
const fittedWidths = new WeakMap();

/**
 * Works out how many swatches show: as many as fit, up to the stylesheet's limit for the
 * breakpoint (4 below md, 5 above)
 * @param {HTMLElement} container - the swatch row
 * @param {HTMLElement} list - the swatch radio group
 * @param {HTMLElement} more - the "+N" link
 * @param {HTMLElement[]} swatches - the swatches, all shown
 * @returns {number} how many swatches to show
 */
function getSwatchLimit(container, list, more, swatches) {
    const containerStyle = window.getComputedStyle(container);

    // Measured at its widest, so the count still fits once N is known
    more.hidden = false; // eslint-disable-line no-param-reassign
    more.textContent = `+${swatches.length}`; // eslint-disable-line no-param-reassign

    return countFittingSwatches({
        available: container.clientWidth - parseFloat(containerStyle.paddingLeft) - parseFloat(containerStyle.paddingRight),
        swatchWidth: swatches[0].offsetWidth,
        gap: parseFloat(window.getComputedStyle(list).columnGap) || 0,
        moreWidth: more.offsetWidth,
        moreGap: parseFloat(containerStyle.columnGap) || 0
    }, swatches.length, parseInt(containerStyle.getPropertyValue("--tile-swatch-max"), 10));
}

/**
 * Keeps a tile's swatches to one line, ending with "+N" for the colours left out. The order is
 * fixed by the server (the tile's own colour first), so swatches are only hidden, never moved.
 * @param {HTMLElement} container - the swatch row
 */
function fitSwatches(container) {
    const list = container.querySelector(".tile-colour-swatch-list");
    const more = container.querySelector(".tile-colour-swatches-more");
    const swatches = list ? Array.from(list.querySelectorAll(SWATCH_SELECTOR)) : [];
    if (!more || !swatches.length) {
        return;
    }

    swatches.forEach((swatch) => {
        swatch.hidden = false; // eslint-disable-line no-param-reassign
    });
    container.classList.add("is-fitted");

    const limit = getSwatchLimit(container, list, more, swatches);
    swatches.forEach((swatch, index) => {
        swatch.hidden = index >= limit; // eslint-disable-line no-param-reassign
    });

    // The tab stop stays on the chosen colour, or moves to the first swatch while that is cut off
    const checked = swatches.find((swatch) => swatch.getAttribute("aria-checked") === "true");
    const tabStop = checked && !checked.hidden ? checked : swatches[0];
    swatches.forEach((swatch) => swatch.setAttribute("tabindex", swatch === tabStop ? "0" : "-1"));

    const remaining = swatches.length - limit;
    more.hidden = remaining === 0;
    more.textContent = `+${remaining}`;
    more.setAttribute("aria-label", remaining === 1
        ? more.getAttribute("data-label-one") || ""
        : (more.getAttribute("data-label") || "").replace("__count__", remaining));
}

/**
 * Refits a swatch row when its width changes; height changes from the fit itself are ignored
 * @param {ResizeObserverEntry[]} entries - the rows that changed size
 */
function onSwatchRowsResized(entries) {
    entries.forEach((entry) => {
        const width = Math.round(entry.contentRect.width);
        if (fittedWidths.get(entry.target) !== width) {
            fittedWidths.set(entry.target, width);
            fitSwatches(entry.target);
        }
    });
}

/**
 * Points a tile picture at new images
 * @param {jQuery} $picture - the picture element
 * @param {string} tileImage - desktop image url
 * @param {string} tileMobileImage - mobile image url
 */
function setPicture($picture, tileImage, tileMobileImage) {
    $picture.find("source").attr("srcset", tileImage);
    $picture.find("img").attr({
        srcset: `${tileMobileImage} 1x, ${tileImage} 2x`,
        src: tileMobileImage
    });
}

/**
 * Shows the chosen colour's images in the tile, including the hover image
 * @param {jQuery} $tile - the product tile
 * @param {jQuery} $swatch - the chosen swatch
 */
function updateTileImages($tile, $swatch) {
    const tileImage = $swatch.attr("data-tile-image");
    const tileMobileImage = $swatch.attr("data-tile-mobile-image");
    if (!tileImage || !tileMobileImage) {
        return;
    }

    const $pictures = $tile.find(".tile-image-wrapper picture");
    setPicture($pictures.eq(0), tileImage, tileMobileImage);

    const $secondPicture = $pictures.eq(1);
    const tileImageSecond = $swatch.attr("data-tile-image-second");
    const tileMobileImageSecond = $swatch.attr("data-tile-mobile-image-second");
    const hasSecondImage = Boolean(tileImageSecond && tileMobileImageSecond);
    if (hasSecondImage) {
        setPicture($secondPicture, tileImageSecond, tileMobileImageSecond);
    }
    $secondPicture.toggleClass("d-none", !hasSecondImage);
}

/**
 * Fills part of the tile with the chosen colour's markup, rendered server side into an inert
 * template beside the swatches
 * @param {jQuery} $tile - the product tile
 * @param {string} templateSelector - selects the colours' templates
 * @param {string} containerSelector - selects the part of the tile to fill
 * @param {string} pid - the chosen colour's product ID
 */
function fillFromSwatchTemplate($tile, templateSelector, containerSelector, pid) {
    const template = $tile.find(templateSelector).filter(function () {
        return this.getAttribute("data-pid") === pid;
    }).get(0);
    const container = $tile.find(containerSelector).get(0);

    if (template && container) {
        container.replaceChildren(template.content.cloneNode(true));
    }
}

/**
 * Shows the chosen colour's price and promo labels
 * @param {jQuery} $tile - the product tile
 * @param {string} pid - the chosen colour's product ID
 */
function updateTilePriceAndLabels($tile, pid) {
    fillFromSwatchTemplate($tile, ".tile-colour-swatch-price", ".tile-body-price", pid);

    // Keeps the labels raised if the image is hovered, as quickView.js does when the hover starts
    const isRaised = $tile.find(".tile-labels .promo-labels").hasClass("show-animation");
    fillFromSwatchTemplate($tile, ".tile-colour-swatch-labels", ".tile-labels", pid);
    $tile.find(".tile-labels .promo-labels").toggleClass("show-animation", isRaised);
}

/**
 * Whether a colour is in the shopper's wishlist, from the header's per-customer list; the tile
 * itself is cached across customers
 * @param {string} pid - the colour's product ID
 * @returns {boolean} true when the colour is in the wishlist
 */
function isInWishlist(pid) {
    const wishlistIds = $(".js-header-wishlist").attr("data-wishlist-ids") || "";
    return wishlistIds.split(",").indexOf(pid) > -1;
}

/**
 * Points the wishlist heart at the chosen colour and shows whether that colour is in the wishlist
 * @param {jQuery} $tile - the product tile
 * @param {jQuery} $previousSwatch - the swatch selected until now, if any
 * @param {jQuery} $swatch - the chosen swatch
 * @param {string} pid - the chosen colour's product ID
 */
function updateWishlistHeart($tile, $previousSwatch, $swatch, pid) {
    const $heart = $tile.find(".wishlistTile");
    const $icon = $heart.find(".js-wishlist-heart");

    // The header's list is not updated when a heart is toggled, so the page keeps its own record
    $previousSwatch.attr("data-in-wishlist", String($icon.hasClass("is-filled")));

    const inWishList = $swatch.is("[data-in-wishlist]") ? $swatch.attr("data-in-wishlist") === "true" : isInWishlist(pid);
    $icon.toggleClass("is-filled", inWishList);

    // wishlistHeart reads the pid through jQuery's data cache, so both are set
    $heart.attr("data-pid", pid).data("pid", pid);
    $tile.closest(".product").attr("data-pid", pid).data("pid", pid);
}

/**
 * Switches the tile to the chosen colour: images, price, promo labels, links, Quick View, the
 * wishlist heart and the GTM data, without leaving the PLP
 * @param {jQuery} $swatch - the chosen swatch
 */
function selectSwatch($swatch) {
    const $tile = $swatch.closest(".product-tile");
    const pid = $swatch.attr("data-pid");

    updateWishlistHeart($tile, $swatch.parent().children(`${SWATCH_SELECTOR}[aria-checked="true"]`), $swatch, pid);

    $swatch.siblings(SWATCH_SELECTOR).attr({ "aria-checked": "false", tabindex: "-1" });
    $swatch.attr({ "aria-checked": "true", tabindex: "0" });

    $tile.find(".tile-link, .tile-colour-swatches-more").attr("href", $swatch.attr("data-url"));
    // GTM reads the tile's data-gtm when it fires, so select_item and later impressions report this colour
    if ($swatch.attr("data-tile-gtm")) {
        $tile.attr("data-gtm", $swatch.attr("data-tile-gtm"));
    }
    updateTileImages($tile, $swatch);
    updateTilePriceAndLabels($tile, pid);

    $tile.find(".quickview, .mobile-quickview-button").each(function () {
        const $button = $(this);
        const href = $button.attr("data-href") || "";
        $button.attr({
            "data-pid": pid,
            "data-href": href.replace(/([?&]pid=)[^&]*/, `$1${encodeURIComponent(pid)}`)
        });
    });
}

/**
 * Returns the swatch an arrow key moves to, skipping those left out of the line
 * @param {jQuery} $swatch - the focused swatch
 * @param {string} key - the key pressed
 * @returns {jQuery|null} the swatch to move to, or null for keys the radio group does not handle
 */
function getTargetSwatch($swatch, key) {
    const $swatches = $swatch.parent().children(SWATCH_SELECTOR).filter(":visible");
    const index = $swatches.index($swatch);
    const last = $swatches.length - 1;

    switch (key) {
        case "ArrowRight":
        case "ArrowDown":
            return $swatches.eq(index >= last ? 0 : index + 1);
        case "ArrowLeft":
        case "ArrowUp":
            return $swatches.eq(index <= 0 ? last : index - 1);
        case "Home":
            return $swatches.eq(0);
        case "End":
            return $swatches.eq(last);
        default:
            return null;
    }
}

module.exports = {
    fitColourSwatches: function () {
        // Without ResizeObserver, rows are fitted when they arrive and again when the window is resized
        const resizeObserver = typeof ResizeObserver === "undefined" ? {
            observe: fitSwatches,
            unobserve: () => {}
        } : new ResizeObserver(onSwatchRowsResized);
        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", () => {
                document.querySelectorAll(`${RESULTS_SELECTOR} ${SWATCHES_SELECTOR}`).forEach(fitSwatches);
            });
        }
        const forEachRow = (node, callback) => {
            if (node.nodeType !== Node.ELEMENT_NODE) {
                return;
            }
            if (node.matches(SWATCHES_SELECTOR)) {
                callback(node);
            }
            node.querySelectorAll(SWATCHES_SELECTOR).forEach(callback);
        };

        // Tiles arrive and leave with "Load more", sorting and filtering
        const mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => forEachRow(node, (row) => resizeObserver.observe(row)));
                mutation.removedNodes.forEach((node) => forEachRow(node, (row) => resizeObserver.unobserve(row)));
            });
        });
        document.querySelectorAll(RESULTS_SELECTOR).forEach((results) => {
            forEachRow(results, (row) => resizeObserver.observe(row));
            mutationObserver.observe(results, { childList: true, subtree: true });
        });
    },
    selectColourSwatch: function () {
        $("body").on("click", SWATCH_SELECTOR, function (e) {
            e.preventDefault();
            selectSwatch($(this));
        });
    },
    navigateColourSwatches: function () {
        // Radio group keyboard pattern: arrows move between colours and select them
        $("body").on("keydown", SWATCH_SELECTOR, function (e) {
            const $target = getTargetSwatch($(this), e.key);
            if (!$target || !$target.length) {
                return;
            }
            e.preventDefault();
            selectSwatch($target);
            $target.trigger("focus");
        });
    }
};
